/**
 * ============================================================================
 * ResetPasswordPage.js - Password Reset Page
 * ============================================================================
 * Allows users to set a new password using a reset token
 * Created: November 27, 2025 - Phase 2C Multi-Tenancy
 * 
 * FEATURES:
 * - Token validation from URL
 * - New password and confirm fields
 * - Password strength requirements
 * - Success redirect to login
 * - Error handling for expired/invalid tokens
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword, logout } = useAuth();

  // Clear any existing auth state so stale sessions don't interfere
  useEffect(() => {
    logout();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get token from URL
  const token = searchParams.get('token');
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Check for token on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset link. Please request a new password reset.');
    }
  }, [token]);
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await resetPassword(token, password);
      
      if (result.success) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Password reset successful! Please sign in with your new password.' },
            replace: true 
          });
        }, 3000);
      } else {
        setError(result.error || 'Password reset failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Clear field error when user types
  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setError('');
  };
  
  // Success state
  if (isSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 40,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          {/* Success Icon */}
          <div style={{
            width: 72,
            height: 72,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: 32
          }}>
            ✅
          </div>
          
          <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#1b4332' }}>
            Password Reset!
          </h1>
          
          <p style={{ 
            margin: '0 0 20px', 
            fontSize: 15, 
            color: '#4b5563',
            lineHeight: 1.5
          }}>
            Your password has been successfully reset. Redirecting you to login...
          </p>
          
          {/* Loading spinner */}
          <div style={{
            display: 'inline-block',
            width: 24,
            height: 24,
            border: '3px solid #e5e7eb',
            borderTopColor: '#2d6a4f',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          
          <div style={{ marginTop: 20 }}>
            <Link 
              to="/login"
              style={{
                fontSize: 14,
                color: '#2d6a4f',
                textDecoration: 'none',
                fontWeight: 500
              }}
            >
              Click here if not redirected
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // No token error state
  if (!token) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 40,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          {/* Error Icon */}
          <div style={{
            width: 72,
            height: 72,
            background: '#fef2f2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: 32
          }}>
            ⚠️
          </div>
          
          <h1 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 700, color: '#b91c1c' }}>
            Invalid Reset Link
          </h1>
          
          <p style={{ 
            margin: '0 0 24px', 
            fontSize: 15, 
            color: '#4b5563',
            lineHeight: 1.5
          }}>
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          
          <Link 
            to="/forgot-password"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#2d6a4f',
              color: '#fff',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 15,
              marginBottom: 12
            }}
          >
            Request New Reset Link
          </Link>
          
          <div>
            <Link 
              to="/login"
              style={{
                fontSize: 14,
                color: '#6b7280',
                textDecoration: 'none'
              }}
            >
              Return to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Reset form
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 40,
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Logo and Title */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 72,
            height: 72,
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            boxShadow: '0 4px 12px rgba(45, 106, 79, 0.1)',
            padding: 6
          }}>
            <img 
              src="/logo.png" 
              alt="Tee Time Organizer" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
            />
          </div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1b4332' }}>
            Set New Password
          </h1>
          <p style={{ margin: '10px 0 0', fontSize: 14, color: '#6b7280' }}>
            Enter your new password below
          </p>
        </div>
        
        {/* Reset Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Password Field */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#374151' 
            }}>
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError('password');
              }}
              placeholder="At least 8 characters"
              disabled={isSubmitting}
              autoComplete="new-password"
              autoFocus
              style={{
                width: '100%',
                padding: '12px 16px',
                border: fieldErrors.password ? '2px solid #ef4444' : '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isSubmitting ? '#f9fafb' : '#fff',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!fieldErrors.password) e.target.style.borderColor = '#2d6a4f';
              }}
              onBlur={(e) => {
                if (!fieldErrors.password) e.target.style.borderColor = '#e5e7eb';
              }}
            />
            {fieldErrors.password && (
              <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.password}
              </div>
            )}
          </div>
          
          {/* Confirm Password Field */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: 8, 
              fontSize: 14, 
              fontWeight: 600, 
              color: '#374151' 
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearFieldError('confirmPassword');
              }}
              placeholder="Re-enter your password"
              disabled={isSubmitting}
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: fieldErrors.confirmPassword ? '2px solid #ef4444' : '2px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 16,
                outline: 'none',
                transition: 'border-color 0.2s',
                backgroundColor: isSubmitting ? '#f9fafb' : '#fff',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                if (!fieldErrors.confirmPassword) e.target.style.borderColor = '#2d6a4f';
              }}
              onBlur={(e) => {
                if (!fieldErrors.confirmPassword) e.target.style.borderColor = '#e5e7eb';
              }}
            />
            {fieldErrors.confirmPassword && (
              <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.confirmPassword}
              </div>
            )}
          </div>
          
          {/* Password Requirements */}
          <div style={{
            padding: '12px 14px',
            background: '#f9fafb',
            borderRadius: 8,
            fontSize: 13,
            color: '#6b7280'
          }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#374151' }}>
              Password Requirements:
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              color: password.length >= 8 ? '#16a34a' : '#6b7280'
            }}>
              <span>{password.length >= 8 ? '✓' : '○'}</span>
              <span>At least 8 characters</span>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              color: password && password === confirmPassword ? '#16a34a' : '#6b7280'
            }}>
              <span>{password && password === confirmPassword ? '✓' : '○'}</span>
              <span>Passwords match</span>
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 14
            }}>
              {error}
            </div>
          )}
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '14px',
              background: isSubmitting ? '#9ca3af' : '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: isSubmitting ? 'none' : '0 2px 8px rgba(45, 106, 79, 0.3)'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) e.target.style.background = '#1b4332';
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.target.style.background = '#2d6a4f';
            }}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>
        
        {/* Back to Login Link */}
        <div style={{
          marginTop: 24,
          textAlign: 'center'
        }}>
          <Link 
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              color: '#2d6a4f',
              textDecoration: 'none',
              fontWeight: 500
            }}
          >
            <span style={{ fontSize: 18 }}>←</span>
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
